import {
  BidderSeat,
  displayBidderSeatName,
  isCrossInventoryServerSeat,
  isScopeLevelBidderSeat,
  isSparkForPubPublisherSeat,
  isSparkForPubSparkSeat,
  PBSTCK_BIDDER_SEAT_CODE,
  productOfBidderSeatType,
  SCOPE_LEVEL_MAPPING_KEY,
} from '@pubstack/common/src/bidder-seat'

/**
 * My bidder seats holds every integration a scope has in one list, split by **how the seat is
 * integrated** rather than by which collection stores it. Which tab a seat lands on, and which tabs
 * exist at all, is decided here rather than inline in the page: the page reads a dozen hooks, and none
 * of this depends on any of them.
 */
/**
 * An array rather than a bare union: the selected tab lives in the URL, and `parseAsStringLiteral` needs
 * the values at runtime to reject a hand-typed `?tab=` instead of rendering a body for a tab that does
 * not exist. Order is the order the bar renders in.
 */
export const SEAT_TABS = ['client-side', 'server-side', 'spark-for-pub', 'spark-demand'] as const

export type SeatTab = (typeof SEAT_TABS)[number]

/**
 * The page renders the shared `Tabs` bar, whose labels are plain strings and which hands a clicked tab
 * back by label rather than by id — so the mapping has to be reversible, and lives next to the tabs
 * themselves rather than inline in the page.
 */
export const SEAT_TAB_LABEL: Record<SeatTab, string> = {
  'client-side': 'Client-side',
  'server-side': 'Server-side',
  'spark-for-pub': 'Spark for Pubs',
  'spark-demand': 'Spark Demand',
}

export const seatTabOfLabel = (label: string): SeatTab | undefined => (Object.keys(SEAT_TAB_LABEL) as SeatTab[]).find((tab) => SEAT_TAB_LABEL[tab] === label)

/**
 * Which tab a seat belongs on — asked of the seat, not of its type alone. `adm` covers both the
 * client-side seat and the legacy server-side one, and only `platform` tells those two apart, so
 * Server-side holds the current `node` seat and the deprecated `adm` one together. That is the same
 * split `seatTypeLabel` renders in the Type column, and the tag is what distinguishes them once there.
 *
 * `spark_for_pubs` is the one type that answers differently for its two halves, and `isInternal` is what
 * separates them. The customer's half is a server-side integration like any other — the publisher enables
 * it on their ad units and reads it beside their other server-side demand — so it belongs on Server-side,
 * where its owner looks for it. Only the Spark team's half, whose placement mappings no customer edits,
 * stays on the Spark for Pubs tab.
 *
 * Falling out on `platform` also answers for a type nobody has added yet, which is the point: a new
 * seat lands beside the integration it actually is instead of vanishing off the screen its owner reads.
 */
export const tabOfSeat = (seat: BidderSeat): SeatTab =>
  seat.type === 'spark_demand' ? 'spark-demand' : isSparkForPubSparkSeat(seat) ? 'spark-for-pub' : seat.platform === 'client' ? 'client-side' : 'server-side'

/**
 * How many media a seat could be mapped on — the denominator behind "Mapped on 3/12 medias".
 *
 * The same rule `mappingSiteFilters` gives the seat page: only the cross-inventory `node` seat bids on
 * everything the scope serves — a One media does server-side bidding through the scope's Prebid Server
 * too — so only it is measured against the union. Every other seat stays inside its own product,
 * because its mapping spreadsheet only ever offers that product's medias: a wider denominator here
 * would count media the numerator can never reach, and the list would read "3/12" for a seat whose own
 * page says "3/3".
 */
export type MediaCounts = {
  /** Ad-managed sites — One's inventory, all a One-stored seat can be mapped on. */
  adManaged: number
  /** Standalone Node sites — same, for every Node-stored seat but `node` itself. */
  pbsStandalone: number
  /** Ad-managed **union** standalone Node sites — a site can be both, and the sum would over-count it. */
  crossInventory: number
}

export const mediaCountOfSeat = (seat: BidderSeat, counts: MediaCounts): number =>
  isCrossInventoryServerSeat(seat) ? counts.crossInventory : productOfBidderSeatType(seat.type) === 'adm' ? counts.adManaged : counts.pbsStandalone

/**
 * Which tabs wear the Pubstack-internal treatment — the shield in the bar, and the scope's Spark settings
 * alongside.
 *
 * Both of them, and for the same reason: since the seat split, neither tab holds anything a customer
 * owns. Spark Demand is Pubstack's own demand outright, and Spark for Pubs now holds only the Spark
 * team's half of each pair — the publisher's half is a server-side integration and lists on Server-side
 * beside the rest of their demand. `visibleSeatTabs` keeps both off a customer's bar entirely; the shield
 * is what tells the staff member reaching them that these are theirs to operate.
 */
export const isInternalSeatTab = (tab: SeatTab): boolean => tab === 'spark-demand' || tab === 'spark-for-pub'

type TabVisibility = {
  isPubstackUser: boolean
  /** `adstack` — the One offer, which is what sells client-side bidding. */
  hasAdManagement: boolean
  /** `pbsStandalone` — the Node offer, which carries both server-side seats. */
  hasNode: boolean
  /**
   * Onboarded on Spark for Pubs *and* granted the feature. A Pubstack account always has the feature.
   * Opens the tab for staff only — a customer creates the seat from the catalog and then reads it on
   * Server-side, so the entitlement never needs a tab of its own.
   */
  canCreateSparkForPubSeat: boolean
  /** `onboardedFeatures.pbstckBidder` — provisioning, not entitlement. */
  isSparkOnboarded: boolean
  /** The tabs that hold at least one seat. See below: entitlement alone would strand seats. */
  tabsWithSeats: readonly SeatTab[]
}

/**
 * Every tab is gated, and on two things rather than one: what the scope is **entitled** to, or what it
 * already **has**.
 *
 * The second half is what keeps the page honest. Entitlements come and go — a scope that drops Node
 * keeps its `node` seats until someone deletes them, and gating on the offer alone would hide the tab
 * holding them, leaving seats that still bid with no screen that admits they exist. The repo already
 * takes this line for the catalog card ("an existing seat is kept as a card rather than dropped so
 * Spark does not silently vanish from the bidder it was set up on"); this is the same rule for tabs.
 *
 * The two Spark tabs are the ones entitlement cannot open on its own: staff is a hard precondition on
 * both, and a customer never sees either however the scope is provisioned. Spark Demand because it is
 * Pubstack's own demand. Spark for Pubs because the split moved everything a publisher owns onto
 * Server-side — leaving that tab holding only the Spark team's internal halves, which the customer's
 * own listing does not even fetch. Shown to them it would be a permanently empty tab inviting them to
 * create a seat that would then appear somewhere else.
 */
export const visibleSeatTabs = ({ isPubstackUser, hasAdManagement, hasNode, canCreateSparkForPubSeat, isSparkOnboarded, tabsWithSeats }: TabVisibility): SeatTab[] => {
  const hasSeats = (tab: SeatTab) => tabsWithSeats.includes(tab)
  const tabs: SeatTab[] = [
    ...(hasAdManagement || hasSeats('client-side') ? (['client-side'] as const) : []),
    ...(hasNode || hasSeats('server-side') ? (['server-side'] as const) : []),
    ...(isPubstackUser && (canCreateSparkForPubSeat || hasSeats('spark-for-pub')) ? (['spark-for-pub'] as const) : []),
    ...(isPubstackUser && (isSparkOnboarded || hasSeats('spark-demand')) ? (['spark-demand'] as const) : []),
  ]

  // Reached only by a scope that owns an offer whose **access** feature its users have not been granted:
  // `scopeOwnsNoOffer` lets it onto the page, and neither flag above opens a tab for it. It has no seats
  // to strand in that case — `tabsWithSeats` would have caught them — so falling back to the two customer
  // integrations costs nothing and beats rendering a bar with no tabs in it.
  return tabs.length > 0 ? tabs : ['client-side', 'server-side']
}

/**
 * Spelled out rather than derived from `SEAT_TAB_LABEL`: "Spark for Pubs" is a name and keeps its case,
 * while the two integrations are not names and read as prose.
 */
const EMPTY_SEAT_TAB_TITLE: Record<SeatTab, string | undefined> = {
  'client-side': 'You don’t have any client-side seat yet.',
  'server-side': 'You don’t have any server-side seat yet.',
  'spark-for-pub': 'You don’t have any Spark for Pubs seat yet.',
  'spark-demand': undefined,
}

/**
 * What an empty tab says instead of listing seats, or nothing when it has no invitation to make.
 *
 * "No results" is the right answer to a filter that matched nothing, and the wrong one to an integration
 * the scope has simply never set up: the second is not a dead end but a place to send someone, and the
 * page already knows where. Every customer seat is created from the catalog, so that is where it points.
 *
 * Spark Demand answers `undefined` and keeps its table. `seatSetupOptions` deliberately leaves it out of
 * the catalog — it is Pubstack putting its own demand on someone else's adapter rather than something a
 * scope integrates — so the invitation would send staff to a page that cannot create the seat. The answer
 * there is "Add spark demand seat" in the page header and "Create all seats" above the table.
 */
export const emptySeatTabInvitation = (tab: SeatTab): string | undefined => EMPTY_SEAT_TAB_TITLE[tab]

/**
 * `mappingCountPerSite` also carries the `'scope'` sentinel — a scope-level mapping, not a per-site
 * activation. Counting raw keys over-reports Spark seats, and would disagree with the seat page.
 */
export const countMappedSites = (seat: BidderSeat): number => Object.keys(seat.mappingCountPerSite ?? {}).filter((key) => key !== SCOPE_LEVEL_MAPPING_KEY).length

/**
 * Pubstack-operated seats are mapped by placement, at scope level: their mappings carry no `siteId`, so
 * the backend records the `'scope'` sentinel and nothing else. Counting media on one therefore always
 * yields zero however completely it is mapped, which is why it answers "mapped?" from the sentinel
 * instead. Both Spark Demand and the Spark team's half of a Spark For Pub pair are mapped this way.
 */
export const isScopeLevelSeat = (seat: BidderSeat): boolean => isScopeLevelBidderSeat(seat)

export const isSeatMapped = (seat: BidderSeat): boolean => (isScopeLevelSeat(seat) ? (seat.mappingCountPerSite?.[SCOPE_LEVEL_MAPPING_KEY] ?? 0) > 0 : countMappedSites(seat) > 0)

/**
 * The Mapping column's order: unmapped first, then by media count.
 *
 * Mapped or not comes first because a media count alone cannot sort a scope-level seat: it is always zero,
 * so on the two Spark tabs every row tied and the column did nothing.
 */
export const compareSeatMapping = (a: BidderSeat, b: BidderSeat): number => Number(isSeatMapped(a)) - Number(isSeatMapped(b)) || countMappedSites(a) - countMappedSites(b)

/**
 * The adapters "Create all Spark Demand seats" considers: compatible **and** switched on in the Spark
 * global config.
 *
 * Two conditions rather than compatibility alone. A seat on an adapter the config leaves off is carried
 * by no stack, so creating one produces a row that bids on nothing. The config gates what gets *created*
 * here; a row that already exists — created before this gate, or on an adapter switched off afterwards —
 * is still explained in the list by `isGloballyDisabled` rather than left unmarked.
 */
export const activatedSparkDemandBidderCodes = (compatibleBidders: readonly { id: string }[], enabledBidderCodes: readonly string[]): string[] =>
  // Catalog **ids**: they are what a seat's `bidderCode` holds, what seat creation expects, and what the
  // global config stores.
  compatibleBidders.map((bidder) => bidder.id).filter((id) => enabledBidderCodes.includes(id))

/**
 * What "Create all Spark Demand seats" creates: every activated adapter that has no `spark_demand` seat
 * yet. Existing seats are skipped rather than reported as an error.
 */
export const missingSparkDemandBidderCodes = (compatibleBidders: readonly { id: string }[], seats: readonly BidderSeat[], enabledBidderCodes: readonly string[]): string[] =>
  activatedSparkDemandBidderCodes(compatibleBidders, enabledBidderCodes).filter((id) => !seats.some((seat) => seat.type === 'spark_demand' && seat.bidderCode === id))

/**
 * Why an internal tab warns: the seats on it can be provisioned, mapped, carried in a stack — and still
 * never called, because the seat that would carry a request to them is gone.
 *
 * Both Spark stacks are Pubstack's own side of the wire, and nothing on the publisher's side points at
 * them by itself: a media's `pbs` stack names a bidder, and the bidder it names is always a seat the
 * customer owns. Spark Demand is reached through the `pbstck-bidder` seat the publisher adds from the
 * catalog. A Spark for Pubs twin is emitted under its publisher half's name, and keeps its own `-int`
 * name once that half is gone — a name no stack references.
 *
 * The tab cannot show this on its own: an uncalled row looks exactly like a working one.
 */
export type UnreachableSeatsWarning = { title: string; detail: string }

/** A Spark for Pubs twin whose publisher half is no longer among the scope's seats. */
const orphanedSparkForPubSeats = (seats: readonly BidderSeat[]): BidderSeat[] =>
  seats.filter((seat) => isSparkForPubSparkSeat(seat) && !seats.some((other) => isSparkForPubPublisherSeat(other) && other.id === seat.publisherBidderSeatId))

/**
 * Nothing to say on a tab holding no internal seats: the empty-tab invitation already owns that case, and
 * warning that absent seats go uncalled is noise.
 *
 * A sentence each rather than one shared: Spark Demand fails for the whole tab at once and is fixed by
 * the publisher adding Spark, while Spark for Pubs fails one pair at a time and has to name which.
 */
export const unreachableSeatsWarning = (tab: SeatTab, seats: readonly BidderSeat[]): UnreachableSeatsWarning | undefined => {
  if (tab === 'spark-demand') {
    const hasSparkDemandSeats = seats.some((seat) => seat.type === 'spark_demand')
    // Asked of `isInternal` as well as of the code: the customer's Spark seat is a `node` seat riding the
    // same adapter, so the code alone would also match an internal seat and never warn.
    const hasCustomerSparkSeat = seats.some((seat) => !seat.isInternal && seat.bidderCode === PBSTCK_BIDDER_SEAT_CODE)
    if (!hasSparkDemandSeats || hasCustomerSparkSeat) return undefined
    return {
      title: 'This scope has no Spark seat',
      detail: 'Spark Demand is called through the Spark seat a publisher adds from the catalog, and this scope has none. The seats below are carried in the stack and mapped, and nothing calls them.',
    }
  }

  if (tab === 'spark-for-pub') {
    const orphans = orphanedSparkForPubSeats(seats)
    if (orphans.length === 0) return undefined
    // Display names, so the sentence names the rows the reader is looking at rather than the `-int`
    // spelling that only exists on disk.
    const names = orphans.map(displayBidderSeatName)
    const isOne = orphans.length === 1
    return {
      title: isOne ? `${names[0]} has no publisher seat` : `${orphans.length} seats have no publisher seat`,
      detail: `A Spark for Pubs seat is called under the name of its publisher half. ${names.join(', ')} ${isOne ? 'no longer has one, so its placements are' : 'no longer have one, so their placements are'} still generated and nothing calls them.`,
    }
  }

  return undefined
}
